import { useEffect, useMemo } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_ALL_STATES,
  GET_CITIES_BY_STATE,
  GET_ZIPCODES_BY_STATE_AND_CITY,
  GetAllStatesResponse,
  GetCitiesByStateResponse,
  GetZipcodesByStateAndCityResponse,
} from '../../../../graphql/queries/getLocationData';

export interface Option {
  label: string;
  value: string;
}

/**
 * 州 → 城市 → 邮编 三级联动选项（沿用 1.0 的 zipcode 基础表 query）。
 *
 * <p>被 `PickupAddressFields`（站点表单 / AB 向导）与 `ModifyAddressModal`（改地址，PRD 4 列对照布局
 * 不能复用前者的两行布局）共用 —— 抽出来是为了让联动逻辑只有一份，两处只重复 JSX。
 *
 * <p><b>为什么调用方要用 AutoComplete 而非 Select</b>：数据源是 zipcode 基础表，实测 1909 个站点里
 * 有 196 个（约 10%）的 city+zipcode 组合不在该表中。严格 Select 会让这些真实地址录不进来，
 * 比解析错更严重。故必须允许自定义输入，并用 [isCustom] 检出后提示运营务必走地址标准化校验。
 */
export const useLocationCascade = (state?: string, city?: string) => {
  const { data: statesData } = useQuery<GetAllStatesResponse>(GET_ALL_STATES);
  const [loadCities, { data: citiesData }] = useLazyQuery<GetCitiesByStateResponse, { state: string }>(
    GET_CITIES_BY_STATE,
  );
  const [loadZipcodes, { data: zipcodesData }] = useLazyQuery<
    GetZipcodesByStateAndCityResponse,
    { state: string; city: string }
  >(GET_ZIPCODES_BY_STATE_AND_CITY);

  useEffect(() => {
    if (state) loadCities({ variables: { state } });
  }, [state, loadCities]);
  useEffect(() => {
    if (state && city) loadZipcodes({ variables: { state, city } });
  }, [state, city, loadZipcodes]);

  const stateOptions = useMemo<Option[]>(
    () => (statesData?.getAllStates ?? []).map((s) => ({ label: s, value: s })),
    [statesData],
  );
  const cityOptions = useMemo<Option[]>(
    () => (citiesData?.getCitiesByState ?? []).map((c) => ({ label: c, value: c })),
    [citiesData],
  );
  const zipcodeOptions = useMemo<Option[]>(
    () => (zipcodesData?.getZipcodesByStateAndCity ?? []).map((z) => ({ label: z, value: z })),
    [zipcodesData],
  );

  /** 自定义输入检测：有值、选项已加载、且值不在选项里。选项未加载时不判（避免首屏误报）。 */
  const isCustom = (val?: string, opts?: Option[]) =>
    !!val && !!opts && opts.length > 0 && !opts.some((o) => o.value === val);

  const hasCustom =
    isCustom(state, stateOptions) || isCustom(city, cityOptions);

  /** AutoComplete 的通用过滤（按 value 子串不区分大小写） */
  const filterOption = (input: string, option?: Option) =>
    (option?.value ?? '').toLowerCase().includes(input.toLowerCase());

  return { stateOptions, cityOptions, zipcodeOptions, isCustom, hasCustom, filterOption };
};
